/**
 * Thin client-side JSON fetch for the account surfaces. Returns a discriminated result
 * so components surface API 4xx/5xx inline (never swallow). Server remains the control;
 * these calls consume the Step-B `/api/account/*` endpoints as-built.
 */
export interface ApiResult<T = unknown> {
  ok: boolean;
  status: number;
  data: T | null;
  error: string | null;
}

export async function apiJson<T = unknown>(
  path: string,
  init?: { method?: string; body?: unknown },
): Promise<ApiResult<T>> {
  try {
    const res = await fetch(path, {
      method: init?.method ?? "GET",
      headers: { "Content-Type": "application/json" },
      body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
    let data: unknown = null;
    try {
      data = await res.json();
    } catch {
      /* empty body */
    }
    const error =
      !res.ok && data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : !res.ok
          ? "Something went wrong. Please try again."
          : null;
    return { ok: res.ok, status: res.status, data: (data as T) ?? null, error };
  } catch {
    return { ok: false, status: 0, data: null, error: "Network error. Please try again." };
  }
}
